import React, {
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { notification } from 'antd';

import ReferencesModal, {
  EMPTY_ID,
} from '@totem/components/common/referencesModal/ReferencesModal';
import DeviceContext from '@totem/components/devices/devicesContainer/deviceContainerContext';
import InputContext from '@totem/components/ui_components/reportSettings/InputContext';
import { FilterField } from '@totem/components/ui_components/reportSettings/types';
import OrganizationContext from '@totem/components/userContext/organizationContext/organizationContext';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import { Params, Reference } from '@totem/types/common';
import {
  Device,
  DeviceQueryResult,
  DeviceQueryResults,
} from '@totem/types/devices';
import { isNotNull } from '@totem/utilities/common';
import {
  DEVICES_ENDPOINT,
  DEVICES_PAGINATION_ENDPOINT,
} from '@totem/utilities/endpoints';
import { sortStringAscending } from '@totem/utilities/tableUtilities';

import '../devices.css';

export const containerConfiguration: FilterField[] = [
  {
    key: 'displayName',
    label: 'Device Name',
    sortField: 'device.displayName',
    filterField: 'displayName',
    multiple: true,
    values: [],
  },
  {
    key: 'organization',
    label: 'Organization',
    sortField: 'organization.name',
    filterField: 'organizationId',
    multiple: true,
    values: [],
  },
  {
    key: 'region',
    label: 'Region',
    sortField: 'region.name',
    filterField: 'regionId',
    multiple: true,
    values: [],
  },
  {
    key: 'building',
    label: 'Building',
    sortField: 'building.name',
    filterField: 'buildingId',
    multiple: true,
    values: [],
  },
  {
    key: 'controlSystem',
    label: 'Control System',
    sortField: 'controlSystem.name',
    filterField: 'controlSystemId',
    multiple: true,
    values: [],
  },
  {
    key: 'type',
    label: 'Type',
    sortField: 'device.type',
    filterField: 'type',
    multiple: true,
    values: [],
  },
  {
    key: 'vendor',
    label: 'Vendor',
    sortField: 'device.vendor',
    filterField: 'vendor',
    multiple: true,
    values: [],
  },
  {
    key: 'operatingSystem',
    label: 'Operating System',
    sortField: 'device.operatingSystem',
    filterField: 'operatingSystem',
    multiple: true,
    values: [],
  },
  {
    key: 'firmware',
    label: 'Firmware',
    sortField: 'device.firmware',
    filterField: 'firmware',
    multiple: true,
    values: [],
  },
  {
    key: 'model',
    label: 'Model',
    sortField: 'device.model',
    filterField: 'attribute:Model',
    multiple: true,
    values: [],
  },
  {
    key: 'isManaged',
    label: 'Managed',
    sortField: '',
    filterField: 'flags.managed.status',
    multiple: true,
    values: [],
  },
  {
    key: 'isMonitored',
    label: 'Monitored',
    sortField: '',
    filterField: 'flags.monitored.status',
    multiple: true,
    values: [],
  },
  {
    key: 'noBackup',
    label: 'No Backup',
    sortField: '',
    filterField: 'noBackup',
    multiple: true,
    values: [],
  },
  {
    key: 'staleBackups',
    label: 'Stale Backups',
    sortField: '',
    filterField: 'staleBackups',
    multiple: true,
    values: [],
  },
  {
    key: 'noAntiVirus',
    label: 'No Anti-Virus',
    sortField: '',
    filterField: 'noVirusProtection',
    multiple: true,
    values: [],
  },
  {
    key: 'incompleteDeviceType',
    label: 'Incomplete Device Type',
    sortField: '',
    filterField: 'incompleteDeviceType',
    multiple: true,
    values: [],
  },
  {
    key: 'operatingSystemPastEndOfSupport',
    label: 'OS Past End Of Support',
    sortField: '',
    filterField: 'operatingSystemEndOfSupport',
    multiple: true,
    values: [],
  },
  {
    key: 'operatingSystemApproachingEndOfSupport',
    label: 'OS Approaching End Of Support',
    sortField: '',
    filterField: 'operatingSystemApproachingEndOfSupport',
    multiple: true,
    values: [],
  },
  {
    key: 'lowDiskSpace',
    label: 'Low Disk Space',
    sortField: '',
    filterField: 'lowDiskSpace',
    multiple: true,
    values: [],
  },
  {
    key: 'keyType',
    label: 'Key Type',
    sortField: '',
    filterField: 'keys.type',
    multiple: true,
    values: [],
  },
  {
    key: 'searchTerm',
    label: 'Search',
    sortField: '',
    filterField: 'displayName',
    multiple: false,
    value: '',
    operator: 'cscontains',
    allowPersistence: false,
  },
  {
    key: 'searchIpAddress',
    label: 'Search IP Address',
    sortField: '',
    filterField: 'ipAddresses',
    multiple: false,
    value: '',
    operator: 'cscontains',
    allowPersistence: false,
  },
  {
    key: 'searchKey',
    label: 'Search MAC Address / Key',
    sortField: '',
    filterField: 'keys.value',
    multiple: false,
    value: '',
    operator: 'cscontains',
    allowPersistence: false,
  },
];

type Props = {
  refresh?: boolean;
  onRecordTotalChanged?: (total: number) => void;
  onDataRefreshRequested?: () => void;
  children?: ReactNode;
  deviceEndPoint?: string;
  deviceFiltersEndPoint?: string;
};

const DevicesContainer = ({
  refresh,
  onRecordTotalChanged,
  onDataRefreshRequested,
  children,
  deviceEndPoint,
  deviceFiltersEndPoint,
}: Props) => {
  const { userProfile, getApiToken } = useContext(UserProfileContext);
  const { regions, buildings, controlSystems } =
    useContext(OrganizationContext);
  const {
    buildParameters,
    setFilterOptions: setContextFilterOptions,
    filterUpdateCount,
    sortingUpdateCount,
    pagingUpdateCount,
  } = useContext(InputContext);

  const [refreshData, setRefreshData] = useState<boolean>(true);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [isSendingUpdate, setIsSendingUpdate] = useState(false);
  const [isFilterOptionsLoaded, setIsFilterOptionsLoaded] =
    useState<boolean>(false);
  const [deviceData, setDeviceData] = useState<DeviceQueryResults>(null);
  const [showReferenceModal, setShowReferenceModal] = useState(false);
  const [selectedDevice, setSelectedDevice] = useState<DeviceQueryResult>(null);
  const [selectedDevices, setSelectedDevices] = useState<DeviceQueryResult[]>(
    [],
  );
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const fetchAbortController = useRef<AbortController | null>(null);

  useEffect(() => {
    if (isNotNull(onRecordTotalChanged)) {
      onRecordTotalChanged(totalRecords);
    }
  }, [onRecordTotalChanged, totalRecords]);

  useEffect(() => {
    if (refresh && isNotNull(onDataRefreshRequested)) {
      setRefreshData(true);

      onDataRefreshRequested();
    }
  }, [refresh, onDataRefreshRequested]);

  useEffect(() => {
    setRefreshData(true);
  }, [
    deviceEndPoint,
    userProfile.organization.id,
    filterUpdateCount,
    pagingUpdateCount,
    sortingUpdateCount,
  ]);

  const selectDevice = (item: DeviceQueryResult) => {
    if (isNotNull(selectedDevices)) {
      const idx = selectedDevices.findIndex(
        (chk) => chk.device.id === item.device.id,
      );
      if (idx < 0) {
        setSelectedDevices([...selectedDevices, item]);
      }
    } else {
      setSelectedDevices([item]);
    }
  };

  const deselectDevice = (item: DeviceQueryResult) => {
    if (isNotNull(selectedDevices)) {
      setSelectedDevices(
        selectedDevices.filter((chk) => chk.device.id !== item.device.id),
      );
    } else {
      setSelectedDevices([]);
    }
  };

  const clearSelectedDevices = () => {
    setSelectedDevices([]);
  };

  useEffect(() => {
    setIsFilterOptionsLoaded(false);
  }, [userProfile.organization.id]);

  // A custom filters endpoint means a cross-org view, whose regions/buildings
  // span organizations and must come from that endpoint. Otherwise use the
  // current organization's hierarchy, which refreshes on organization switch.
  const useOrganizationHierarchy = !isNotNull(deviceFiltersEndPoint);

  useEffect(() => {
    if (!useOrganizationHierarchy) {
      return;
    }

    const toOptions = (items: { id: string; name: string }[]) =>
      [...(items ?? [])]
        .sort((chkA, chkB) => sortStringAscending(chkA.name, chkB.name))
        .map((item) => ({ text: item.name, value: item.id }));

    setContextFilterOptions('region', toOptions(regions));

    setContextFilterOptions('building', toOptions(buildings));

    setContextFilterOptions('controlSystem', toOptions(controlSystems));
  }, [useOrganizationHierarchy, regions, buildings, controlSystems]);

  useEffect(() => {
    if (!isFilterOptionsLoaded) {
      const deviceFiltersUrl = isNotNull(deviceFiltersEndPoint)
        ? deviceFiltersEndPoint
        : `${DEVICES_ENDPOINT}/filters`;

      fetch(`${deviceFiltersUrl}`, {
        method: 'GET',
        headers: new Headers({
          Authorization: `Bearer ${getApiToken()}`,
        }),
      })
        .then((res) => res.json())
        .then((result) => {
          if (isNotNull(result)) {
            if (isNotNull(result.type)) {
              setContextFilterOptions(
                'type',
                result.type.map((opt) => ({
                  text: opt.name,
                  value: opt.value,
                })),
              );
            }
            if (isNotNull(result.vendor)) {
              setContextFilterOptions(
                'vendor',
                result.vendor.map((opt) => ({
                  text: opt.name,
                  value: opt.value,
                })),
              );
            }
            if (isNotNull(result.operatingSystem)) {
              setContextFilterOptions(
                'operatingSystem',
                result.operatingSystem.map((opt) => ({
                  text: opt.name,
                  value: opt.value,
                })),
              );
            }
            if (isNotNull(result.firmware)) {
              setContextFilterOptions(
                'firmware',
                result.firmware.map((opt) => ({
                  text: opt.name,
                  value: opt.value,
                })),
              );
            }
            if (isNotNull(result.models)) {
              setContextFilterOptions(
                'model',
                result.models.map((opt) => ({
                  text: opt.name,
                  value: opt.value,
                })),
              );
            }
            if (!useOrganizationHierarchy && isNotNull(result.region)) {
              setContextFilterOptions(
                'region',
                result.region.map((opt) => ({
                  text: opt.name,
                  value: opt.value,
                })),
              );
            }
            if (!useOrganizationHierarchy && isNotNull(result.building)) {
              setContextFilterOptions(
                'building',
                result.building.map((opt) => ({
                  text: opt.name,
                  value: opt.value,
                })),
              );
            }
            if (isNotNull(result.organization)) {
              setContextFilterOptions(
                'organization',
                result.organization.map((opt) => ({
                  text: opt.name,
                  value: opt.value,
                })),
              );
            }
          }
        })
        .then(() => {
          setIsFilterOptionsLoaded(true);
        });
    }
  }, [isFilterOptionsLoaded, useOrganizationHierarchy, getApiToken]);

  useEffect(() => {
    if (refreshData) {
      fetchAbortController.current?.abort();

      const controller = new AbortController();

      fetchAbortController.current = controller;

      setRefreshData(false);

      setIsLoading(true);
      const params: Params = buildParameters();

      const deviceUrl = isNotNull(deviceEndPoint)
        ? deviceEndPoint
        : DEVICES_PAGINATION_ENDPOINT;

      fetch(`${deviceUrl}`, {
        method: 'POST',
        headers: new Headers({
          Authorization: `Bearer ${getApiToken()}`,
        }),
        body: JSON.stringify(params),
        signal: controller.signal,
      })
        .then((res) => res.json())
        .then((result: DeviceQueryResults) => {
          setDeviceData(result);

          setTotalRecords(result.paging.totalRecords);
        })
        .then(() => {
          setIsLoading(false);
        })
        .catch((err: Error) => {
          if (err.name !== 'AbortError') {
            setIsLoading(false);
          }
        });
    }
  }, [refreshData, refresh, getApiToken]);

  useEffect(() => {
    if (isDeleting === true && isNotNull(selectedDevice)) {
      setIsDeleting(false);

      fetch(`${DEVICES_ENDPOINT}/${selectedDevice.device.id}`, {
        method: 'DELETE',
        headers: new Headers({
          Authorization: `Bearer ${getApiToken()}`,
        }),
      }).then((res) => {
        if (res.status < 400) {
          setIsDeleting(false);

          setSelectedDevice(null);

          setRefreshData(true);
        } else {
          notification.error({
            title: 'Error removing device!',
            description: 'An error occurred while removing the device.',
            duration: 0,
          });

          setIsDeleting(false);

          setSelectedDevice(null);
        }
      });
    }
  }, [isDeleting, selectedDevice, getApiToken]);

  const getDeviceReferences = () => {
    let ref: Reference = {
      organizationId: EMPTY_ID,
      regionId: EMPTY_ID,
      buildingId: EMPTY_ID,
      controlSystemId: EMPTY_ID,
    };

    if (isNotNull(selectedDevice)) {
      ref = {
        organizationId: selectedDevice.device.organizationId,
        regionId: selectedDevice.device.regionId,
        buildingId: selectedDevice.device.buildingId,
        controlSystemId: selectedDevice.device.controlSystemId,
      };
    }
    return ref;
  };

  const sendDeviceReferenceUpdate = useCallback(
    async (device: Device) => {
      if (isSendingUpdate) {
        return;
      }

      setIsSendingUpdate(true);

      fetch(`${DEVICES_ENDPOINT}/references`, {
        method: 'POST',
        headers: new Headers({
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getApiToken()}`,
        }),
        body: JSON.stringify(device),
      }).then(() => {
        setIsSendingUpdate(false);

        setShowReferenceModal(false);

        setRefreshData(true);
      });
    },
    [isSendingUpdate],
  );

  const handleSubmitReferences = (reference: Reference) => {
    const deviceUpdate = {
      ...selectedDevice.device,
      organizationId: reference.organizationId,
      regionId: reference.regionId,
      buildingId: reference.buildingId,
      controlSystemId: reference.controlSystemId,
    };

    setSelectedDevice(null);

    sendDeviceReferenceUpdate(deviceUpdate);
  };

  const handleAction = (action: string, device: DeviceQueryResult) => {
    // eslint-disable-next-line default-case
    switch (action) {
      case 'refresh':
        setRefreshData(true);
        break;
      case 'ShowReferenceModal':
        setSelectedDevice(device);

        setShowReferenceModal(true);
        break;
      case 'RemoveDevice':
        setSelectedDevice(device);

        setIsDeleting(true);
        break;
    }
  };

  return (
    <DeviceContext.Provider
      value={{
        loading: isLoading,
        deviceData,
        selectedDevices,
        selectDevice,
        deselectDevice,
        clearSelectedDevices,
        totalRecords,
        onAction: handleAction,
      }}
    >
      <div>{children}</div>
      {showReferenceModal && isNotNull(selectedDevice) && (
        <ReferencesModal
          title={`Assign: ${selectedDevice.device.displayName}`}
          isVisible={showReferenceModal}
          references={getDeviceReferences()}
          onSubmit={handleSubmitReferences}
          onClose={() => {
            setShowReferenceModal(false);

            setSelectedDevice(null);
          }}
          showBuilding
          showControlSystem
          showOrganization={false}
          showRegion
        />
      )}
    </DeviceContext.Provider>
  );
};

export default DevicesContainer;
